import { Check } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeader from "@/components/SectionHeader";
import CTASection from "@/components/CTASection";
import EditorialMarquee from "@/components/Marquee";
import { Reveal } from "@/components/Reveal";
import { IMG } from "@/data/content";

const SOLUTIONS = [
  {
    number: "01",
    title: "Guest Rooms",
    text: "Everything a guest touches from the moment they enter — bedding that presents beautifully and performs through daily turnover.",
    items: ["Bed Sheets", "Duvets", "Duvet Covers", "Pillows", "Mattress Protectors", "Bed Runners", "Cushions", "Blankets"],
    image: IMG.bedroom2,
  },
  {
    number: "02",
    title: "Bathrooms",
    text: "Soft, absorbent bath textiles that hold up to frequent laundering while keeping a premium feel for every guest.",
    items: ["Bath Towels", "Pool Towels", "Hand Towels", "Face Towels", "Bath Mats", "Bath Robes"],
    image: IMG.towels,
  },
  {
    number: "03",
    title: "Dining & Hospitality Areas",
    text: "Considered linen for restaurants, banquets and shared hospitality spaces — the finishing detail guests notice.",
    items: ["Table Napkins", "Hospitality Linen Products"],
    image: IMG.dining,
  },
];

const Solutions = () => (
  <main data-testid="solutions-page">
    <PageHero
      eyebrow="Hospitality Solutions"
      title="Hospitality Textile Solutions for"
      italic="Hotels, Resorts & Institutions"
      description="Amazing Grace offers a broad range of products that support hospitality businesses across every guest touchpoint — rooms, bathrooms and dining — supplied for hospitality requirements across India."
      image={IMG.lobby}
    />

    {SOLUTIONS.map((s, i) => (
      <section
        key={s.number}
        data-testid={`solution-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        className={`py-24 md:py-32 ${i % 2 === 1 ? "bg-linen/40" : ""}`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal className={`relative ${i % 2 === 1 ? "lg:order-2" : ""}`}>
            <div className={`absolute w-full h-full border border-gold/50 pointer-events-none ${i % 2 === 1 ? "-top-4 -right-4" : "-bottom-4 -left-4"}`} />
            <div className="overflow-hidden group">
              <img
                src={s.image}
                alt={s.title}
                className="w-full h-[380px] md:h-[520px] object-cover transition-transform duration-[1600ms] ease-out group-hover:scale-[1.04]"
              />
            </div>
          </Reveal>
          <div className={i % 2 === 1 ? "lg:order-1" : ""}>
            <SectionHeader number={s.number} eyebrow="Solution" title={s.title} />
            <Reveal delay={0.15}>
              <p className="mt-6 text-navy/70 text-base md:text-lg font-light leading-relaxed">{s.text}</p>
              <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mt-10">
                {s.items.map((item) => (
                  <li key={item} className="flex items-center gap-3 text-[15px] text-navy/80">
                    <Check size={15} className="text-gold shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>
    ))}

    <EditorialMarquee />
    <CTASection
      title="Looking for Hospitality Textiles in Bulk?"
      text="Whether you are furnishing a new property, upgrading an existing hotel or sourcing for your business, talk to our team about your requirements."
      image={IMG.resort}
    />
  </main>
);

export default Solutions;
